/**
 * MicroEAI Fairness Control — Enterprise Governance (LLM)
 * Constants, types, and risk logic aligned with Python fairness control.
 * Eligibility: SS = min(N_A, N_B) >= SS_THRESHOLD, PRR >= PRR_THRESHOLD.
 * Risk = 1 - readiness, readiness = eligible/required (AUDIT).
 */

export const SS_THRESHOLD = 3;
export const PRR_THRESHOLD = 0.25;
export const RISK_GREEN_MAX = 0.25;
export const RISK_AMBER_MAX = 0.5;

export const STATE_NONE = 'NONE';
export const STATE_PARTIAL = 'PARTIAL';
export const STATE_ELIGIBLE = 'ELIGIBLE';
export const STATE_EXCLUDED = 'EXCLUDED';

export type FairnessState = typeof STATE_NONE | typeof STATE_PARTIAL | typeof STATE_ELIGIBLE | typeof STATE_EXCLUDED;

export interface ComplianceRow {
  Attribute: string;
  Mode: string;
  State: FairnessState;
  N_A: number;
  N_B: number;
  SS: number;
  PRR: number;
  Src: string;
}

export interface RiskDict {
  ModeUsed: string;
  Risk: number;
  Band: string;
  Required: number;
  Eligible: number;
  Unfulfilled: number;
  Readiness: number;
  GovernanceState: string;
  Reason: string;
}

function bandFromRisk(risk: number): string {
  if (risk <= RISK_GREEN_MAX) return 'GREEN (Low)';
  if (risk <= RISK_AMBER_MAX) return 'AMBER (Moderate)';
  return 'RED (High)';
}

function fairnessRequiredCount(rows: ComplianceRow[]): number {
  return rows.filter(r => r.State === STATE_PARTIAL || r.State === STATE_ELIGIBLE).length;
}

function fairnessEligibleCount(rows: ComplianceRow[]): number {
  return rows.filter(r => r.State === STATE_ELIGIBLE).length;
}

/**
 * Compute MicroEAI Fairness Risk and GovernanceState from compliance and audit rows.
 * Mirrors Python microeai_fairness_risk_score().
 */
export function microeaiFairnessRiskScore(compRows: ComplianceRow[], auditRows: ComplianceRow[]): RiskDict {
  const complianceRequired = fairnessRequiredCount(compRows);
  const auditRequired = fairnessRequiredCount(auditRows);
  const auditEligible = fairnessEligibleCount(auditRows);
  const auditPartial = auditRows.filter(r => r.State === STATE_PARTIAL).length;

  if (complianceRequired > 0 && auditRequired === 0) {
    return {
      ModeUsed: 'AUDIT',
      Risk: 0,
      Band: 'POLICY_ACCEPTED',
      Required: complianceRequired,
      Eligible: 0,
      Unfulfilled: 0,
      Readiness: 1,
      GovernanceState: 'POLICY_ACCEPTED',
      Reason: 'All fairness-required attributes were excluded by reviewer policy'
    };
  }

  if (complianceRequired === 0 && auditRequired === 0) {
    return {
      ModeUsed: 'AUDIT',
      Risk: 0,
      Band: bandFromRisk(0),
      Required: 0,
      Eligible: 0,
      Unfulfilled: 0,
      Readiness: 1,
      GovernanceState: 'CLEAN',
      Reason: 'No fairness-required attributes detected'
    };
  }

  if (auditRequired > 0) {
    const readiness = auditEligible / auditRequired;
    const risk = 1 - readiness;
    return {
      ModeUsed: 'AUDIT',
      Risk: risk,
      Band: bandFromRisk(risk),
      Required: auditRequired,
      Eligible: auditEligible,
      Unfulfilled: auditRequired - auditEligible,
      Readiness: readiness,
      GovernanceState: 'ACTIVE_RISK',
      Reason: `Fairness-required attrs in audit scope: eligible=${auditEligible}, partial=${auditPartial}`
    };
  }

  return {
    ModeUsed: 'AUDIT',
    Risk: 0,
    Band: bandFromRisk(0),
    Required: 0,
    Eligible: 0,
    Unfulfilled: 0,
    Readiness: 1,
    GovernanceState: 'UNKNOWN',
    Reason: 'Unexpected state'
  };
}

/**
 * Derive compliance-style rows from legacy ML_Fairness_Metric (Attribute, Disparate Impact, Equalized Odds, Risk).
 * Maps Risk Low -> ELIGIBLE, Moderate/High -> PARTIAL; uses DI to approximate N_A/N_B balance.
 */
export function deriveComplianceFromLegacyMetric(
  mlMetrics: Array<{ Attribute?: string; 'Disparate Impact'?: number; 'Equalized Odds'?: number; Risk?: string }>
): ComplianceRow[] {
  if (!Array.isArray(mlMetrics) || mlMetrics.length === 0) {
    return [];
  }

  const safeNum = (v: unknown): number => {
    const n = Number(v);
    return typeof n === 'number' && !Number.isNaN(n) && isFinite(n) ? n : 0;
  };

  return mlMetrics.map((row, idx) => {
    const attr = row.Attribute ?? (row as any).attribute ?? `Attribute ${idx + 1}`;
    const di = safeNum(row['Disparate Impact'] ?? (row as any).disparateImpact ?? (row as any).disparate_impact);
    const risk = String(row.Risk ?? (row as any).risk ?? '').toLowerCase();

    // Approximate N_A, N_B from Disparate Impact (DI = N_minority / N_majority). Use 1 and 1/DI scaled.
    let N_A = 1;
    let N_B = 1;
    if (di > 0 && di <= 1) {
      N_A = Math.max(1, Math.round(1 / di));
      N_B = 1;
    } else if (di > 1) {
      N_A = 1;
      N_B = Math.max(1, Math.round(di));
    }
    const SS = Math.min(N_A, N_B);
    const PRR = Math.max(N_A, N_B) > 0 ? Math.min(N_A, N_B) / Math.max(N_A, N_B) : 0;
    const meetsEligibility = SS >= SS_THRESHOLD && PRR >= PRR_THRESHOLD;

    let State: FairnessState = STATE_NONE;
    if (risk === 'low' && meetsEligibility) {
      State = STATE_ELIGIBLE;
    } else if (risk === 'low' || risk === 'moderate' || risk === 'high') {
      State = STATE_PARTIAL;
    }

    return {
      Attribute: attr,
      Mode: 'COMPLIANCE',
      State,
      N_A,
      N_B,
      SS,
      PRR: Math.round(PRR * 1000) / 1000,
      Src: 'Output'
    };
  });
}

/**
 * Count by state for bar charts (ELIGIBLE, PARTIAL, NONE, EXCLUDED).
 */
export function countByState(rows: ComplianceRow[]): {
  ELIGIBLE: number;
  PARTIAL: number;
  NONE: number;
  EXCLUDED: number;
} {
  return {
    ELIGIBLE: rows.filter(r => r.State === STATE_ELIGIBLE).length,
    PARTIAL: rows.filter(r => r.State === STATE_PARTIAL).length,
    NONE: rows.filter(r => r.State === STATE_NONE).length,
    EXCLUDED: rows.filter(r => r.State === STATE_EXCLUDED).length
  };
}
